import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import {
	ErrorMessageViewModel,
	IAccount,
	IMeetingContext,
	INote,
	NoteViewModel,
	RichContentContextSource,
} from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { DefaultEditableNoteContentCSS, IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { IAdminUserSessionComponentProps, InternalMeetingNoteType } from '../../../../models/AdminModels';
import { UserSessionViewModelKey } from '../../../../models/AppState';
import { ToolbarAdminNotes } from '../../../../models/UiUtils';
import { DeprecatedCloseButton } from '../../../../web/components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../web/components/Modal';
import { NoteEditor } from '../../../../web/components/notes/NoteEditor';
import { MeetingContext } from '../../../../web/components/richContent/MeetingContext';
import { IRichContentDocumentEditorConfig } from '../../../../web/components/richContent/RichContentDocumentEditor';
import { NotesIcon } from '../../../../web/components/svgs/icons/NotesIcon';
import { white } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IAdminUserSessionComponentProps, IModalContext<INote> {
	account?: IAccount;
	className?: string;
	note: NoteViewModel;
	onAccountUpdated?(updatedAccount: IAccount): void;
	onNextMeetingSelected(nextMeeting: Date): void;
	styles?: StyleDeclarationValue[];
	type: InternalMeetingNoteType;
}

const DefaultEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	contentHorizontalPadding: 20,
	contentRawCss: DefaultEditableNoteContentCSS,
	minHeight: 100,
	toolbar: ToolbarAdminNotes,
};

interface IState {
	accountStage?: string;
	adsStage?: string;
	hideCCField?: boolean;
}

class _AccountDetailsNoteComposer extends React.Component<IProps, IState> {
	public readonly state: IState = {
		hideCCField: true,
	};

	public render() {
		const { account, className, styles, userSession, note, onNextMeetingSelected, type } = this.props;
		const { hideCCField } = this.state;
		let showNextMeeting = false;
		let nextMeetingToShow;
		if (type === InternalMeetingNoteType.CSM) {
			showNextMeeting = true;
			nextMeetingToShow = account?.additionalInfo?.nextMeeting;
		} else if (type === InternalMeetingNoteType.Websites) {
			showNextMeeting = true;
			nextMeetingToShow = account?.additionalInfo?.nextWebsitesMeeting;
		} else if (
			type === InternalMeetingNoteType.Ads ||
			type === InternalMeetingNoteType.AdsFacebook ||
			type === InternalMeetingNoteType.AdsGoogleLsa ||
			type === InternalMeetingNoteType.AdsGooglePpc
		) {
			showNextMeeting = true;
			// Per-channel next-ads-meeting fields (nextFacebookAdsMeeting etc.) are auto-populated
			// server-side. The manual setter writes to overall nextAdsMeeting only, so the composer
			// surfaces the overall value for every ads channel — by design.
			nextMeetingToShow = account?.additionalInfo?.nextAdsMeeting;
		}
		return (
			<div
				className={`${css(styleSheet.container, ...(styles || []))} account-details-note-composer ${className || ''}`}
			>
				<div className={css(styleSheet.header)}>
					<NotesIcon fillColor={white} />
					<DeprecatedCloseButton fillColor={white} onClick={this.onCancelClicked} />
				</div>
				<div className={css(styleSheet.body)}>
					<div className={css(styleSheet.editorContainer)}>
						{this.renderSubheader()}
						<NoteEditor
							account={account}
							alwaysShowsSaveOptions={true}
							autoFocus={true}
							className={css(styleSheet.adminNoteModal, !hideCCField && styleSheet.adminNoteModalCCOpen)}
							editorConfig={DefaultEditorConfig}
							footerStyles={[styleSheet.editorFooter]}
							hideMeetingAttendeesHeader={true}
							hideMentionField={true}
							hidesCCField={hideCCField}
							hidesPrivacyToggle={true}
							isPublicNote={true}
							note={note}
							onCancel={this.onCancelClicked}
							onNextMeetingSelected={onNextMeetingSelected}
							onNoteSaved={this.onSaveSuccess}
							onValidateBeforeSaving={this.onValidateBeforeSaving}
							sendMessageDisabled={true}
							showNextMeeting={showNextMeeting}
							nextMeetingToShow={nextMeetingToShow}
							sizeConstraint='normal'
							styles={[styleSheet.editor]}
							toastOnSuccess={false}
							userSession={userSession}
						/>
					</div>
					{this.renderContext()}
				</div>
				{!!note?.isBusy && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} />}
			</div>
		);
	}

	private renderSubheader() {
		const { type, account } = this.props;
		const { hideCCField } = this.state;
		let typeDescription: React.ReactNode = null;
		switch (type) {
			case InternalMeetingNoteType.Websites:
			case InternalMeetingNoteType.CSM:
			case InternalMeetingNoteType.Ads:
			case InternalMeetingNoteType.AdsFacebook:
			case InternalMeetingNoteType.AdsGoogleLsa:
			case InternalMeetingNoteType.AdsGooglePpc: {
				typeDescription = 'a note';
				break;
			}
			case InternalMeetingNoteType.Sales: {
				typeDescription = (
					<span>
						<span className={css(baseStyleSheet.fontBold)}>sales notes</span>
					</span>
				);
				break;
			}
			case InternalMeetingNoteType.Summary: {
				typeDescription = (
					<span>
						{'a '}
						<span className={css(baseStyleSheet.fontBold)}>summary</span>
						{' note'}
					</span>
				);
				break;
			}
			default: {
				break;
			}
		}
		return (
			<>
				<div className={css(styleSheet.subheader)}>
					<span>
						Add &nbsp;
						{typeDescription}
						&nbsp;
						{`about ${account?.companyName || ''}`}
					</span>
				</div>
				{hideCCField && (
					<div className={css(styleSheet.additionalControlsContainer)}>
						<button className={css(baseStyleSheet.brandLink)} onClick={this.showCCField}>
							<span>Add Cc</span>
						</button>
					</div>
				)}
			</>
		);
	}

	private showCCField = () => {
		this.setState({
			hideCCField: false,
		});
	};

	private renderContext() {
		const { account, note, type } = this.props;

		const meetingContext = note.context as IMeetingContext;

		if (note?.context?.source !== RichContentContextSource.Meeting) {
			return null;
		}
		return (
			<div className={css(styleSheet.editorContext)}>
				<MeetingContext
					account={account}
					internalMeetingNoteType={type}
					meetingContext={meetingContext}
					onContextChanged={this.onMeetingContextChanged}
				/>
			</div>
		);
	}

	private onValidateBeforeSaving = (note: INote, errorMessages?: ErrorMessageViewModel) => {
		const meetingContext = note.context as IMeetingContext;
		if (meetingContext?.source === RichContentContextSource.Meeting && !meetingContext?.meetingType) {
			errorMessages?.push({
				messages: ['Please select a meeting type.'],
			});
			return false;
		}
		return true;
	};

	private onMeetingContextChanged = (meetingContext: IMeetingContext) => {
		const { note } = this.props;
		note.setContext(meetingContext);

		// Track accountStage and adsStage in local state for saving
		// Use 'in' check so clearing (setting to undefined) is also captured
		const stateUpdate: Partial<IState> = {};
		if ('accountStage' in meetingContext) {
			stateUpdate.accountStage = meetingContext.accountStage;
		}
		if ('adsStage' in meetingContext) {
			stateUpdate.adsStage = meetingContext.adsStage;
		}
		if (Object.keys(stateUpdate).length > 0) {
			this.setState(stateUpdate as IState);
		}
	};

	private onCancelClicked = () => {
		const { parentModal } = this.props;

		parentModal?.onRequestClose(null, true);
	};

	private onSaveSuccess = async (note: INote) => {
		const { account, logApiError, onAccountUpdated, parentModal, userSession, type } = this.props;
		const { accountStage, adsStage } = this.state;

		// Update account stage if it was changed
		if (accountStage && account?.id) {
			try {
				const updatedAccount = await userSession.webServiceHelper.callAsync<IAccount>(
					`admin/account/${account.id}/additionalInfo/stage?stage=${encodeURIComponent(accountStage)}`,
					'PUT'
				);
				// Notify parent so it can update its state with the full account object
				onAccountUpdated?.(updatedAccount);
			} catch (error) {
				// Log error but don't prevent modal from closing since note was saved
				logApiError('UpdateAccountStage-Error', error);
				toast.error('Note saved, but failed to update account stage');
			}
		}

		// Update ads stage if it was changed
		if (adsStage && account?.id) {
			try {
				const updatedAccount = {
					...account,
					additionalInfo: { ...account.additionalInfo, adsStage },
				};
				const saved = await userSession.webServiceHelper.callAsync<IAccount>(
					`admin/account/${account.id}`,
					'PUT',
					updatedAccount
				);
				onAccountUpdated?.(saved);
			} catch (error) {
				logApiError('UpdateAdsStage-Error', error);
				toast.error('Note saved, but failed to update ads stage');
			}
		}

		// Optimistically update no-show flags based on the meeting status we just saved.
		// Skip for Ads (catch-all + per-channel): addAccountMeetingNote already refetches the full account from the server,
		// and spreading the stale `account` prop here would clobber that fresh state (issue #18440).
		const meetingContext = note?.context as IMeetingContext;
		if (
			meetingContext?.source === RichContentContextSource.Meeting &&
			type !== InternalMeetingNoteType.Ads &&
			type !== InternalMeetingNoteType.AdsFacebook &&
			type !== InternalMeetingNoteType.AdsGoogleLsa &&
			type !== InternalMeetingNoteType.AdsGooglePpc
		) {
			const isNoShow = meetingContext.meetingStatus === 'No showed';
			const health = { ...(account?.additionalInfo?.health || {}) };
			if (type === InternalMeetingNoteType.CSM) {
				health.lastCsmMeetingNoShow = isNoShow;
			} else if (type === InternalMeetingNoteType.Websites) {
				health.lastWebMeetingNoShow = isNoShow;
			}
			const updatedAccount: IAccount = {
				...account,
				additionalInfo: { ...account.additionalInfo, health },
			};
			onAccountUpdated?.(updatedAccount);
		}

		parentModal?.onRequestClose(note, false);
	};
}

const AccountDetailsNoteComposerAsObserver = observer(_AccountDetailsNoteComposer);
const AccountDetailsNoteComposerWithContext = inject(
	UserSessionViewModelKey,
	ModalChildComponentContextKey
)(AccountDetailsNoteComposerAsObserver);
const AccountDetailsNoteComposer = withEventLogging(
	AccountDetailsNoteComposerWithContext,
	'AccountDetailsNoteComposer'
);

export const AccountDetailsNoteComposerModal = asModalComponent(AccountDetailsNoteComposer, {
	className: `${css(styleSheet.modal)} AccountDetailsNoteComposerModal`,
	shouldCloseOnOverlayClick: false,
});
